<script setup>
import BaseButton from "./BaseButton.vue";
import BaseDropdown from "./BaseDropdown.vue";
import SvgIcon from "./SvgIcon.vue";

// A primary action fused with a caret that opens a dropdown of alternatives.
// Primary click emits `click`; pass the menu items (`.list-item` <li>s) via the
// default slot. Looks like one outlined pill with a divider before the caret.
defineProps({
  label: { type: String, required: true },
  menuLabel: { type: String, default: "More options" },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
defineEmits(["click"]);
</script>

<template>
  <div class="split-button">
    <BaseButton variant="outlined" :loading :disabled @click.prevent="$emit('click')">
      {{ label }}
    </BaseButton>
    <BaseDropdown align="right" :disabled="disabled || loading">
      <template #trigger>
        <button type="button" class="caret" :aria-label="menuLabel">
          <SvgIcon name="system/chevron-down" width="16" height="16" aria-hidden="true" />
        </button>
      </template>
      <slot />
    </BaseDropdown>
  </div>
</template>

<style scoped lang="scss">
@use "@/colors" as c;

.split-button {
  display: inline-flex;
  align-items: stretch;

  // Primary half: flatten the corners that meet the caret.
  :deep(.base-button.outlined) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

// Caret half: match the outlined primary, reuse its right edge as the divider,
// round only the outer corners.
.caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 8px;
  cursor: pointer;
  color: c.$text-subtle;
  background-color: c.$background-ui;
  border: 1px solid c.$main;
  border-left: none;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;

  &:hover {
    color: c.$main;
    background-color: c.$background-grey-light;
  }
}
</style>
